import React from 'react';
import { Info, GraduationCap, FileText, History, Calendar, Settings, CreditCard, ShoppingBag, Calculator } from 'lucide-react';

interface ServiceCard {
  id: string;
  title: string;
  icon: string;
  color: string;
}

interface ServicesGridProps {
  title: string;
  subtitle: string;
  services: ServiceCard[];
  onServiceClick: (serviceId: string) => void;
}

const ServicesGrid: React.FC<ServicesGridProps> = ({ title, subtitle, services, onServiceClick }) => {
  const getServiceIcon = (iconType: string) => {
    const iconClass = "w-8 h-8 text-white";
    
    switch (iconType) {
      case 'banner':
        return (
          <div className="w-16 h-16 bg-blue-500 rounded-lg flex items-center justify-center">
            <Settings className={iconClass} />
          </div>
        );
      case 'grades':
        return (
          <div className="w-16 h-16 bg-green-500 rounded-lg flex items-center justify-center">
            <GraduationCap className={iconClass} />
          </div>
        );
      case 'history':
        return (
          <div className="w-16 h-16 bg-purple-500 rounded-lg flex items-center justify-center">
            <History className={iconClass} />
          </div>
        );
      case 'events':
        return (
          <div className="w-16 h-16 bg-orange-500 rounded-lg flex items-center justify-center">
            <Calendar className={iconClass} />
          </div>
        );
      case 'servicenow':
        return (
          <div className="w-16 h-16 bg-teal-500 rounded-lg flex items-center justify-center">
            <Settings className={iconClass} />
          </div>
        );
      case 'billing':
        return (
          <div className="w-16 h-16 bg-red-500 rounded-lg flex items-center justify-center">
            <CreditCard className={iconClass} />
          </div>
        );
      case 'store':
        return (
          <div className="w-16 h-16 bg-yellow-500 rounded-lg flex items-center justify-center">
            <ShoppingBag className={iconClass} />
          </div>
        );
      case 'account':
        return (
          <div className="w-16 h-16 bg-indigo-500 rounded-lg flex items-center justify-center">
            <Calculator className={iconClass} />
          </div>
        );
      default:
        return (
          <div className="w-16 h-16 bg-gray-500 rounded-lg flex items-center justify-center">
            <FileText className={iconClass} />
          </div>
        );
    }
  };

  return (
    <section className="py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-800">{title}</h2>
            <p className="text-gray-600 mt-2">{subtitle}</p>
          </div>
          <button className="text-teal-600 hover:text-teal-700 font-medium flex items-center space-x-1">
            <span>Ver más</span>
            <span>→</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service) => (
            <div 
              key={service.id} 
              onClick={() => onServiceClick(service.id)}
              className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow relative group cursor-pointer"
            >
              <div className="flex flex-col items-center text-center space-y-4">
                {getServiceIcon(service.icon)}
                <h3 className="font-bold text-gray-800">{service.title}</h3>
              </div>
              
              {/* Info button */}
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onServiceClick(service.id);
                }}
                className="absolute bottom-4 right-4 w-8 h-8 bg-teal-600 hover:bg-teal-700 rounded-full flex items-center justify-center text-white transition-colors"
              >
                <Info size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;